// 合同模板类型定义
export interface ContractTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  fileName: string;
  uploadTime: string;
  variables: string[]; // 模板中识别出的变量列表
  isSystem?: boolean; // 是否为系统预制模板（可区分显示）
  systemPath?: string; // 系统模板文件路径（public/templates/下）
  fileData?: string; // 重新上传的文件内容（base64，优先于 systemPath）
}

export type TemplateCategory =
  | '框架合同'
  | '采购合同'
  | '销售合同'
  | '补充协议'
  | '保密协议'
  | '服务协议'
  | '法务函'
  | '租赁合同'
  | '工程服务'
  | '合作协议'
  | '终止协议'
  | '其他';

export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  '框架合同',
  '采购合同',
  '销售合同',
  '补充协议',
  '保密协议',
  '服务协议',
  '法务函',
  '租赁合同',
  '工程服务',
  '合作协议',
  '终止协议',
  '其他',
];

// 公司创建者身份
export type CompanyCreator = 'admin' | 'user' | 'system';

// 公司角色：区分甲方/乙方/通用
export type CompanyRole = 'partyA' | 'partyB' | 'both';

// 公司信息类型定义
export interface CompanyInfo {
  id: string;
  fullName: string; // 公司全称（必填）
  creditCode: string; // 统一社会信用代码
  legalRepresentative: string; // 法定代表人
  registeredAddress: string; // 注册地址
  officeAddress: string; // 办公地址
  phone: string; // 联系电话
  fax: string; // 传真号码
  email: string; // 电子邮箱
  bankName: string; // 开户银行
  bankAccount: string; // 银行账号
  taxId: string; // 纳税人识别号
  role: CompanyRole; // 公司角色：甲方/乙方/通用
  createdBy: CompanyCreator; // 创建者身份：admin/user/system
  creatorId?: string; // 创建者的用户ID（用于区分不同员工）
  updatedAt: string; // 最后更新时间
}

// 合同生成请求
export interface ContractGenerationRequest {
  templateId: string;
  ourCompanyId: string;
  partyB: CompanyInfo; // 乙方信息
  contractNumber: string;
  signingDate: string;
  startDate?: string;
  endDate?: string;
}

// 变量映射
export interface VariableMapping {
  [key: string]: string; // 变量名 -> 对应字段
}

// 替换变量类型（用于合同生成）
export interface ReplaceVariables {
  [key: string]: string;
}

// 分期付款期数
export interface PaymentInstallment {
  period: number;       // 第几期
  percentage: string;   // 付款比例，如 "30%"
  amount: string;       // 付款金额（小写）
}

// 采购明细行
export interface PurchaseItem {
  产品名称: string;
  规格型号: string;
  数量: string;
  单价: string;
  金额: string;       // 自动计算 = 数量 × 单价
}

// 合同核心条款
export interface ContractTerms {
  paymentMethod: '一次性付款' | '分期付款' | '月结' | '季结' | '其他';
  paymentDetails: string;        // 付款详情说明
  installments: PaymentInstallment[];  // 分期明细（分期付款时可用）
  validityPeriod: string;      // 合同有效期描述
  specialClauses: string;     // 特殊条款
  purchaseItems: PurchaseItem[]; // 采购明细
  totalAmount: string;        // 总金额（小写）
  totalAmountUppercase: string; // 总金额（大写）
}

// 合同生成记录
export type ContractStatus = '待审核' | '已通过' | '已驳回';

export interface ContractRecord {
  id: string;
  contractNumber: string;    // 合同编号
  templateName: string;       // 使用的模板名称
  templateCategory: string;    // 合同类型
  ourCompanyName: string;      // 我方公司
  partyBName: string;         // 对方公司
  employeeName: string;        // 使用人姓名
  employeeCompany: string;     // 使用人所属公司
  employeePhone: string;       // 使用人手机号
  createdAt: string;          // 生成时间
  contractTerms?: string;      // 核心条款摘要
  status: ContractStatus;      // 审核状态
  reviewComment?: string;      // 审核意见
  reviewedBy?: string;        // 审核人
  reviewedAt?: string;        // 审核时间
}

// 标准变量名
export const STANDARD_VARIABLES = {
  // 我方公司
  OUR_FULL_NAME: '{{我方公司全称}}',
  OUR_CREDIT_CODE: '{{我方统一社会信用代码}}',
  OUR_LEGAL_REP: '{{我方法定代表人}}',
  OUR_REG_ADDRESS: '{{我方注册地址}}',
  OUR_OFFICE_ADDRESS: '{{我方办公地址}}',
  OUR_PHONE: '{{我方联系电话}}',
  OUR_FAX: '{{我方传真}}',
  OUR_EMAIL: '{{我方邮箱}}',
  OUR_BANK: '{{我方开户银行}}',
  OUR_BANK_ACCOUNT: '{{我方银行账号}}',
  OUR_TAX_ID: '{{我方纳税人识别号}}',
  // 乙方公司
  PARTY_B_FULL_NAME: '{{乙方公司全称}}',
  PARTY_B_CREDIT_CODE: '{{乙方统一社会信用代码}}',
  PARTY_B_LEGAL_REP: '{{乙方法定代表人}}',
  PARTY_B_REG_ADDRESS: '{{乙方注册地址}}',
  PARTY_B_OFFICE_ADDRESS: '{{乙方办公地址}}',
  PARTY_B_PHONE: '{{乙方联系电话}}',
  PARTY_B_FAX: '{{乙方传真}}',
  PARTY_B_EMAIL: '{{乙方邮箱}}',
  PARTY_B_BANK: '{{乙方开户银行}}',
  PARTY_B_BANK_ACCOUNT: '{{乙方银行账号}}',
  PARTY_B_TAX_ID: '{{乙方纳税人识别号}}',
  // 合同通用
  CONTRACT_NUMBER: '{{合同编号}}',
  CONTRACT_SIGN_DATE: '{{合同签订日期}}',
  CONTRACT_START_DATE: '{{合同生效日期}}',
  CONTRACT_END_DATE: '{{合同到期日期}}',
  // 合同核心条款
  PAYMENT_METHOD: '{{付款方式}}',
  PAYMENT_DETAILS: '{{付款详情}}',
  VALIDITY_PERIOD: '{{合同有效期}}',
  SPECIAL_CLAUSES: '{{特殊条款}}',
};

// 员工身份信息
export interface EmployeeInfo {
  name: string;        // 姓名
  company: string;     // 所属公司
  phone: string;       // 手机号
  updatedAt: string;   // 最后更新时间
}

// 本地存储键名
export const STORAGE_KEYS = {
  TEMPLATES: 'contract_templates',
  COMPANIES: 'contract_companies',
  VARIABLE_MAPPINGS: 'contract_variable_mappings',
  EMPLOYEE_INFO: 'contract_employee_info',
  CONTRACT_RECORDS: 'contract_records',
};
